"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import type { ProductDetail, ProductRow } from "@/lib/types/product";
import type { ProductFormValues } from "@/lib/schemas/product";

function toPayload(values: ProductFormValues): Record<string, unknown> {
  return {
    name: values.name,
    brand: values.brand || null,
    sku: values.sku || null,
    barcode: values.barcode || null,
    category_id: values.category_id ?? null,
    unit_id: values.unit_id ?? null,
    hsn_code: values.hsn_code || null,
    gst_rate: values.gst_rate,
    cost_price: values.cost_price,
    selling_price: values.selling_price,
    mrp: values.mrp,
    stock_qty: values.stock_qty,
    branch_id: values.branch_id ?? null,
    is_active: values.is_active === false ? 0 : 1,
  };
}

export function useCreateProduct() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (values: ProductFormValues) => {
      const { data } = await api.post<{ product: ProductDetail }>(
        "/api/products",
        toPayload(values),
      );

      return data.product;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      values,
    }: {
      id: number;
      values: ProductFormValues;
    }) => {
      const { data } = await api.put<{ product: ProductDetail }>(
        `/api/products/${id}`,
        toPayload(values),
      );

      return data.product;
    },
    onMutate: async ({ id, values }) => {
      await qc.cancelQueries({ queryKey: ["products"] });
      const payload = toPayload(values) as Partial<ProductRow>;
      const previous = qc.getQueriesData<{ data: ProductRow[]; pagination: unknown }>({
        queryKey: ["products"],
      });
      qc.setQueriesData<{ data: ProductRow[]; pagination: unknown }>(
        { queryKey: ["products"] },
        (old) => {
          if (!old?.data) {
            return old;
          }

          return {
            ...old,
            data: old.data.map((row) =>
              row.id === id ? ({ ...row, ...payload } as ProductRow) : row,
            ),
          };
        },
      );

      return { previous };
    },
    onError: (_e, _v, ctx) => {
      ctx?.previous?.forEach(([key, data]) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: (_d, _e, vars) => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      if (vars?.id) {
        void qc.invalidateQueries({ queryKey: ["product", vars.id] });
      }
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/products/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useUploadProductImage() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post<{ image_url: string }>(
        `/api/products/${id}/image`,
        fd,
      );

      return data.image_url;
    },
    onSuccess: (_url, { id }) => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      void qc.invalidateQueries({ queryKey: ["product", id] });
    },
  });
}

export function useImportProducts() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post<{
        success_count: number;
        error_count: number;
        errors: { row: number; message: string }[];
      }>("/api/products/import", fd);

      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}
